<script setup lang="ts">
/**
 * 告警頁（未解決告警；operator 可讀＋可 ack）。
 *
 * - **分組可切換**：依主機（一台 VM 出事會同時噴主機層與站台層告警，攤平成單一長表看不出
 *   因果）或依種類（同一類問題橫跨多台時，一次看完＝一次處理完）。
 * - 兩個開關對應後端 query：`include_suppressed`（父子抑制：主機離線期間的子告警照寫但預設濾掉）、
 *   `include_resolved`（已自動關閉的歷史）。
 * - **已確認（acked）的列預設隱藏**（2026-09-09 主人回報）：事件式告警 ack 過之後仍留在
 *   未解決表裡，整頁被「處理過的」洗版，真正待辦的反而看不到。這是**前端過濾**——
 *   後端沒有 `exclude_acked` 參數，而清單本來就一次撈回來；開關狀態記 localStorage
 *   （每個瀏覽器各自記，不進伺服器）。
 * - 30 秒輪詢；ack 後立即 refresh。批次確認＝逐筆打 ack（後端沒有批次端點），
 *   失敗的筆數會回報，不會默默吞掉。
 * - **keyset 分頁**（批93-D）：後端回 `{items, next_cursor}`。這一頁刻意用**一頁 200 筆**
 *   （＝改分頁之前的行為，第一次載入的內容一個字都沒變），超過才出現「載入更多」。
 *   ★ 輪詢時會把**已載入的頁數**一起重抓——只抓第一頁的話，使用者每按一次「載入更多」
 *   都會在 30 秒後被打回原形。上限 `MAX_PAGES` 頁：再多請用上方的過濾條件縮小範圍
 *   （告警多到一千筆時，該做的是處理告警不是往下捲）。
 */
import { computed, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { HostAlert } from '@/api/hosting'
import { alertDetailText, alertKindLabel, alertTargetText, severityLabel } from '@/lib/fleet'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { usePolling } from '@/lib/poll'
import { fmtSeen } from '@/lib/staleness'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const dialogs = useDialogsStore()

const alerts = ref<HostAlert[]>([])
const loading = ref(true)
const loadError = ref('')
/** 分組維度。依種類分組時「同一類跨多台」一次看完，處理起來是同一套動作。 */
type GroupMode = 'host' | 'kind'
/**
 * 批186：顯示開關與分組維度記住（輪詢頁，每次進來重設會讓正在追某一批告警的人一直重勾）。
 * `showAcked`＝「顯示已確認」，預設關＝只看還沒有人處理的。要在 `usePolling` 之前呼叫。
 */
const pf = usePersistedFilters(
  'alerts',
  { includeSuppressed: false, includeResolved: false, showAcked: false, groupMode: 'host' as GroupMode },
  { oneOf: { groupMode: ['host', 'kind'] } },
)
const { includeSuppressed, includeResolved, showAcked, groupMode } = pf.refs
const { dirty: filtersDirty } = pf
/** 前兩個開關由下面的 `watch` 重載；其餘只是前端顯示，改值即可。 */
const clearFilters = () => pf.clear()
// 舊鍵沒分帳號，不搬進分帳號的記憶（同 CS 批42 裁示）；清掉免得殘留。
try {
  localStorage.removeItem('ipo_ls_alerts_show_acked')
} catch {
  /* 不可用就算了 */
}
/** 一頁幾筆（＝批93-D 之前的固定上限，第一次載入的內容因此完全沒變）。 */
const PAGE = 200
/** 輪詢重抓時最多重走幾頁——同時也是「載入更多」的上限（200×5＝1000 筆）。 */
const MAX_PAGES = 5
/** keyset 續頁游標（null＝已經載完）。 */
const nextCursor = ref<string | null>(null)
/** 目前載了幾頁（輪詢要把同樣多的頁重抓回來，見檔頭）。 */
const pagesLoaded = ref(1)
const loadingMore = ref(false)
/** 批次確認勾選的告警 id（只收還沒 ack 的；ack 過的沒有勾選框）。 */
const picked = ref<string[]>([])
const ackingBatch = ref(false)

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  return typeof d === 'string' ? d : JSON.stringify(d)
}

function alertQuery(cursor: string | null) {
  return {
    includeSuppressed: includeSuppressed.value,
    includeResolved: includeResolved.value,
    limit: PAGE,
    cursor,
  }
}

async function load() {
  try {
    // ★ 重抓**已載入的頁數**而不是只抓第一頁（見檔頭）。頁數天生有上限，
    //   而且提早在 `next_cursor` 為 null 時就收手，所以常態只會是一次往返。
    const acc: HostAlert[] = []
    let cursor: string | null = null
    for (let i = 0; i < pagesLoaded.value; i++) {
      const page = await hosting.listHostAlerts(alertQuery(cursor))
      acc.push(...page.items)
      cursor = page.next_cursor
      if (!cursor) break
    }
    alerts.value = acc
    nextCursor.value = cursor
    // 勾選過的列可能已經不在清單裡（被別人 ack／自動解決）→ 順手剔掉，免得批次確認打 404。
    const live = new Set(alerts.value.filter((a) => !a.acked_at).map((a) => a.id))
    picked.value = picked.value.filter((id) => live.has(id))
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取告警失敗')
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    const page = await hosting.listHostAlerts(alertQuery(nextCursor.value))
    alerts.value = [...alerts.value, ...page.items]
    nextCursor.value = page.next_cursor
    pagesLoaded.value += 1
  } catch (e) {
    loadError.value = errText(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

const poll = usePolling(load, 30_000)
// 改過濾條件＝重新從第一頁開始（游標是跟著 WHERE 走的，換條件不能沿用舊游標）。
watch([includeSuppressed, includeResolved], () => {
  pagesLoaded.value = 1
  nextCursor.value = null
  void poll.refresh()
})

interface Group {
  key: string
  title: string
  /** 依主機分組時才有（可連到主機詳情）。 */
  hostId: string | null
  rows: HostAlert[]
  crit: number
  warn: number
  info: number
}

/** 已確認但仍在未解決表裡的筆數（＝關掉開關時被藏起來的量，要讓人知道有這些東西）。 */
const ackedCount = computed(() => alerts.value.filter((a) => !!a.acked_at).length)
/** 這一頁真正要畫的列（預設濾掉已確認的）。totals／分組／頁首計數全部吃這一份。 */
const visibleAlerts = computed(() =>
  showAcked.value ? alerts.value : alerts.value.filter((a) => !a.acked_at),
)

/** 組序＝crit 多的在前、再 warn、再標題（組內維持後端的 severity → 時間排序）。 */
const groups = computed<Group[]>(() => {
  const byHost = groupMode.value === 'host'
  const map = new Map<string, Group>()
  for (const a of visibleAlerts.value) {
    // 批169：沒有主機的部署層告警（自部署站的配額用量）自成一組，不和「中台層待辦」混在一起。
    const deployNoHost = !a.host_id && a.scope === 'deploy'
    const key = byHost ? (a.host_id ?? (deployNoHost ? '__deploy__' : '__none__')) : a.kind
    let g = map.get(key)
    if (!g) {
      g = {
        key,
        title: byHost
          ? (a.host_name ??
            (a.host_id ? a.host_id.slice(0, 8) : deployNoHost ? '不在代管主機上的站台' : '（未指定主機）'))
          : alertKindLabel(a.kind),
        hostId: byHost ? a.host_id : null,
        rows: [],
        crit: 0,
        warn: 0,
        info: 0,
      }
      map.set(key, g)
    }
    g.rows.push(a)
    if (a.severity === 'crit') g.crit += 1
    else if (a.severity === 'warn') g.warn += 1
    else g.info += 1
  }
  return [...map.values()].sort(
    (a, b) => b.crit - a.crit || b.warn - a.warn || a.title.localeCompare(b.title),
  )
})

// --- 批次確認 ---
function ackable(a: HostAlert): boolean {
  return !a.acked_at
}
function togglePick(id: string) {
  picked.value = picked.value.includes(id)
    ? picked.value.filter((x) => x !== id)
    : [...picked.value, id]
}
function groupAllPicked(g: Group): boolean {
  const ids = g.rows.filter(ackable).map((a) => a.id)
  return ids.length > 0 && ids.every((id) => picked.value.includes(id))
}
function toggleGroup(g: Group) {
  const ids = g.rows.filter(ackable).map((a) => a.id)
  if (groupAllPicked(g)) picked.value = picked.value.filter((id) => !ids.includes(id))
  else picked.value = [...new Set([...picked.value, ...ids])]
}

async function ackPicked() {
  const ids = picked.value.slice()
  if (!ids.length) return
  const ok = await dialogs.confirm({
    title: '批次確認告警',
    message: `把 ${ids.length} 筆告警標記為已確認？「確認」只是記下「有人看過了」——問題還在的話告警不會消失，也不會停止對帳。`,
    confirmText: '確認',
  })
  if (!ok) return
  ackingBatch.value = true
  let failed = 0
  try {
    // 後端沒有批次 ack 端點；逐筆打，失敗只記數不中斷（部分成功也是有價值的）。
    for (const id of ids) {
      try {
        await hosting.ackHostAlert(id)
      } catch {
        failed += 1
      }
    }
    picked.value = []
    await poll.refresh()
    if (failed) {
      await dialogs.notify({
        title: '部分失敗',
        message: `${ids.length - failed} 筆已確認，${failed} 筆失敗（可能已被別人處理或已解決）。`,
      })
    }
  } finally {
    ackingBatch.value = false
  }
}

const totals = computed(() => ({
  crit: visibleAlerts.value.filter((a) => a.severity === 'crit').length,
  warn: visibleAlerts.value.filter((a) => a.severity === 'warn').length,
  info: visibleAlerts.value.filter((a) => a.severity === 'info').length,
}))

const acking = ref<string | null>(null)
async function ack(a: HostAlert) {
  acking.value = a.id
  try {
    await hosting.ackHostAlert(a.id)
    await poll.refresh()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, 'ack 失敗') })
  } finally {
    acking.value = null
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>告警</h1>
      <span class="count">{{ visibleAlerts.length }}</span>
      <div class="tot">
        <span v-if="totals.crit" class="sev crit">嚴重 {{ totals.crit }}</span>
        <span v-if="totals.warn" class="sev warn">警告 {{ totals.warn }}</span>
        <span v-if="totals.info" class="sev info">提示 {{ totals.info }}</span>
      </div>
      <span class="spacer" />
      <div class="tabs gmode" data-hscroll>
        <button type="button" class="tab" :class="{ active: groupMode === 'host' }" @click="groupMode = 'host'">
          依主機
        </button>
        <button type="button" class="tab" :class="{ active: groupMode === 'kind' }" @click="groupMode = 'kind'">
          依種類
        </button>
      </div>
      <label class="opt"><input v-model="includeSuppressed" type="checkbox" /> 顯示被抑制的</label>
      <label class="opt"><input v-model="includeResolved" type="checkbox" /> 顯示已解決</label>
      <label
        class="opt"
        title="已確認＝有人看過並處理中。預設隱藏，避免處理過的告警把真正待辦的洗掉。"
      >
        <input v-model="showAcked" type="checkbox" /> 顯示已確認
        <span v-if="!showAcked && ackedCount" class="opt-n">（{{ ackedCount }}）</span>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton
        v-if="picked.length"
        variant="primary"
        size="sm"
        :loading="ackingBatch"
        @click="ackPicked"
      >
        確認選取（{{ picked.length }}）
      </UiButton>
    </div>

    <p v-if="includeSuppressed" class="hintbox">
      「被抑制」＝該主機正處於離線告警期間，底下站台層的告警照樣記錄但預設不列出——
      一台 VM 掛掉會連帶噴出站台離線、備份過期、憑證檢查失敗等十幾條，全部列出來只會蓋掉真正的因。
      主機恢復後子告警會自己回到清單（或自動解決）。
    </p>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <template v-else>
      <p v-if="loadError" class="err">{{ loadError }}</p>

      <div v-if="!groups.length" class="card">
        <div class="empty">
          <div class="empty-title">
            {{ ackedCount && !showAcked ? '沒有待處理的告警' : '目前沒有告警' }}
          </div>
          <div class="empty-sub">
            <template v-if="ackedCount && !showAcked">
              還有 {{ ackedCount }} 筆<strong>已確認</strong>的告警未解決——勾選上方「顯示已確認」可以看到它們。
            </template>
            <template v-else>
              主機離線、磁碟水位、憑證／授權到期、備份過期等都會自動出現在這裡（背景每 60 秒對帳一次）；
              站台的配額用量（例如總容量）越過憑證設計宣告的門檻也會列在這裡（每 5 分鐘對帳一次）。
            </template>
          </div>
        </div>
      </div>

      <div v-for="g in groups" :key="g.key" class="card">
        <div class="card-head">
          <label class="gpick" title="全選這一組還沒確認的告警">
            <input type="checkbox" :checked="groupAllPicked(g)" @change="toggleGroup(g)" />
          </label>
          <h3 class="section-title">
            <RouterLink v-if="g.hostId" :to="{ name: hr('host-detail'), params: { id: g.hostId } }">{{ g.title }}</RouterLink>
            <span v-else>{{ g.title }}</span>
          </h3>
          <span v-if="g.crit" class="badge">{{ g.crit }}</span>
          <span v-if="g.warn" class="badge warn">{{ g.warn }}</span>
          <span v-if="g.info" class="badge info">{{ g.info }}</span>
        </div>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th class="pick-h"></th>
                <th>級別</th><th>{{ groupMode === 'kind' ? '主機' : '種類' }}</th><th>對象</th>
                <th>說明</th><th>首次</th><th>最近</th><th>確認</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in g.rows" :key="a.id" :class="{ dim: a.suppressed || !!a.acked_at }">
                <td class="pick">
                  <input
                    v-if="ackable(a)"
                    type="checkbox"
                    :checked="picked.includes(a.id)"
                    @change="togglePick(a.id)"
                  />
                </td>
                <td><span class="sev" :class="a.severity">{{ severityLabel(a.severity) }}</span></td>
                <td class="nowrap">
                  <template v-if="groupMode === 'kind'">
                    <RouterLink v-if="a.host_id" :to="{ name: hr('host-detail'), params: { id: a.host_id } }">
                      {{ a.host_name ?? a.host_id.slice(0, 8) }}
                    </RouterLink>
                    <span v-else>{{ a.scope === 'deploy' ? '—' : '（未指定主機）' }}</span>
                  </template>
                  <template v-else>
                    {{ alertKindLabel(a.kind) }}
                  </template>
                  <span v-if="a.suppressed" class="sub">被主機離線抑制</span>
                </td>
                <td class="nowrap">
                  <RouterLink v-if="a.site_slug && a.host_id" :to="{ name: hr('host-detail'), params: { id: a.host_id } }">
                    {{ a.site_slug }}
                  </RouterLink>
                  <span v-else>{{ alertTargetText(a) }}</span>
                </td>
                <td>{{ alertDetailText(a) }}</td>
                <td class="nowrap tsmall">{{ fmtSeen(a.first_seen, { short: true }) }}</td>
                <td class="nowrap tsmall">{{ fmtSeen(a.last_seen, { short: true }) }}</td>
                <td class="acts-cell">
                  <span v-if="a.acked_at" class="subtle">
                    {{ a.acked_by || '已確認' }}<br />{{ fmtSeen(a.acked_at, { short: true }) }}
                  </span>
                  <UiButton v-else size="sm" :loading="acking === a.id" @click="ack(a)">確認</UiButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- keyset 分頁：一頁 200 筆。`next_cursor` 非 null 才畫這一列（形狀與 LicensesView 一致）。 -->
      <div v-if="nextCursor && pagesLoaded < MAX_PAGES" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已載入 {{ alerts.length }} 筆</span>
      </div>
      <p v-else-if="nextCursor" class="more subtle">
        已載入 {{ alerts.length }} 筆（顯示上限）。還有更多告警——請用上方的過濾條件縮小範圍。
      </p>
    </template>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.tot {
  display: flex;
  gap: 6px;
  margin-left: var(--sp-2);
}
.spacer {
  flex: 1;
}
.opt {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
/* 「顯示已確認」關著時的被藏筆數（別讓人以為那些告警不見了） */
.opt-n {
  color: var(--fg-4);
}
.gmode {
  margin-right: var(--sp-2);
}
.hintbox {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: var(--st-transferred-bg);
  color: var(--st-transferred-fg);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.gpick {
  display: inline-flex;
  align-items: center;
}
.pick-h,
td.pick {
  width: 1%;
  white-space: nowrap;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.card-head .section-title {
  margin: 0;
}
.card-head .section-title a {
  color: var(--brand-700);
  text-decoration: none;
}
[data-theme='dark'] .card-head .section-title a {
  color: var(--brand-400);
}
.card-head .section-title a:hover {
  text-decoration: underline;
}
.card-head .badge {
  margin-left: 0;
}
.nowrap {
  white-space: nowrap;
}
/* 注意：`display:block` 只能給 td **內**的 span——套在 td 本身會讓儲存格脫離表格列
   （踩過：首次／最近兩欄疊成上下兩行）。儲存格自己的小字用 `.tsmall`。 */
.sub {
  display: block;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.tsmall {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
tr.dim {
  opacity: 0.62;
}
/* keyset 分頁的「載入更多」列（形狀與 LicensesView／ResellerLicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  margin: 0;
}
</style>
